/**
 * BW20-C — three horizontal macro bars (Carbs, Protein, Fat) with green
 * target band, lower-bound tick mark, and drift coloring.
 *
 * The lower-bound tick is the load-bearing element here: under-fueling on a
 * given macro is the primary risk for a plant-forward endurance athlete on
 * GLP-1, and the tick makes "I am below the floor" instantly visible.
 */

import { useMemo } from "react";

export type MacroTargetBand = {
  min_g: number;
  max_g: number;
};

export type DailyDiaryMacrosProps = {
  carbs: { logged_g: number; band: MacroTargetBand };
  protein: { logged_g: number; band: MacroTargetBand };
  fat: { logged_g: number; band: MacroTargetBand };
};

export function DailyDiaryMacros({
  carbs,
  protein,
  fat,
}: DailyDiaryMacrosProps) {
  return (
    <ul
      role="list"
      style={{
        listStyle: "none",
        padding: 0,
        margin: 0,
        display: "grid",
        gap: "var(--space-3)",
      }}
    >
      <MacroBar label="Carbs" unit="g" {...carbs} />
      <MacroBar label="Protein" unit="g" {...protein} />
      <MacroBar label="Fat" unit="g" {...fat} />
    </ul>
  );
}

function MacroBar({
  label,
  unit,
  logged_g,
  band,
}: {
  label: string;
  unit: string;
  logged_g: number;
  band: MacroTargetBand;
}) {
  const { tone, fillPct, lowerTickPct, scaleMax } = useMemo(() => {
    const max = Math.max(logged_g, band.max_g) * 1.1;
    const fill = Math.min(100, (logged_g / max) * 100);
    const lower = (band.min_g / max) * 100;
    let t: "neutral" | "ok" | "warn" | "off";
    if (logged_g === 0) t = "neutral";
    else if (logged_g >= band.min_g && logged_g <= band.max_g) t = "ok";
    else {
      const drift =
        logged_g < band.min_g
          ? (band.min_g - logged_g) / Math.max(1, band.min_g)
          : (logged_g - band.max_g) / Math.max(1, band.max_g);
      t = drift <= 0.15 ? "warn" : "off";
    }
    return { tone: t, fillPct: fill, lowerTickPct: lower, scaleMax: max };
  }, [logged_g, band.min_g, band.max_g]);

  const fillColor: Record<typeof tone, string> = {
    neutral: "var(--fg-4, #999)",
    ok: "var(--haus-green-600, #4a9c52)",
    warn: "var(--haus-amber-500, #d99a3a)",
    off: "var(--haus-red-500, #b04a30)",
  };

  // Green target band rendered as a translucent strip on the bar.
  const bandLeftPct = lowerTickPct;
  const bandWidthPct = Math.max(
    0.5,
    ((band.max_g - band.min_g) / Math.max(1, scaleMax)) * 100,
  );

  return (
    <li>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-body-s)",
            color: "var(--fg-2)",
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--type-body-s)",
            color: "var(--fg-3)",
          }}
        >
          {Math.round(logged_g)} / {band.min_g}–{band.max_g} {unit}
        </span>
      </div>
      <div
        role="img"
        aria-label={`${label} ${Math.round(logged_g)} ${unit}, target band ${band.min_g} to ${band.max_g} ${unit}.`}
        style={{
          position: "relative",
          height: 10,
          borderRadius: 5,
          background: "var(--bg-2, #ececec)",
          overflow: "hidden",
        }}
      >
        {/* Green target band */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 0,
            left: `${bandLeftPct}%`,
            width: `${bandWidthPct}%`,
            height: "100%",
            background: "var(--haus-green-600, #4a9c52)",
            opacity: 0.18,
          }}
        />
        {/* Logged fill */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: `${fillPct}%`,
            background: fillColor[tone],
            transition: "width 200ms ease-out",
          }}
        />
        {/* Lower-bound tick — the load-bearing element. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: -2,
            bottom: -2,
            left: `${lowerTickPct}%`,
            width: 2,
            background: "var(--fg-1)",
            opacity: 0.55,
          }}
        />
      </div>
    </li>
  );
}
